import { useCallback, useEffect, useState } from 'react';
import { Ban, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiFetch } from './apiBase';

const TYPES = [
  { id: 'oracleElement', label: 'Oracle element', placeholder: 'e.g. Commission Target' },
  { id: 'adpWageTypeCode', label: 'ADP wage type code', placeholder: 'e.g. 3008' },
];

export default function ReconExclusions() {
  const [data, setData] = useState({ oracleElements: [], adpWageTypeCodes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [addType, setAddType] = useState('oracleElement');
  const [addValue, setAddValue] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [bulkType, setBulkType] = useState('oracleElement');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await apiFetch('/api/recon/exclusions');
      setData(d);
    } catch (e) {
      const missing =
        e.status === 404 && /route not found/i.test(String(e.message || ''));
      setError(
        missing
          ? 'Could not load exclusions. Refresh the page or try again.'
          : e.message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 3500);
  };

  const addOne = async (e) => {
    e.preventDefault();
    const value = addValue.trim();
    if (!value) return;
    setSaving(true);
    setError('');
    try {
      const { exclusions } = await apiFetch('/api/recon/exclusions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: addType, value }),
      });
      setData(exclusions);
      setAddValue('');
      flash(`Excluded ${value}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addBulk = async (e) => {
    e.preventDefault();
    const lines = bulkText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return;
    setSaving(true);
    setError('');
    try {
      let latest = data;
      for (const value of lines) {
        const { exclusions } = await apiFetch('/api/recon/exclusions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: bulkType, value }),
        });
        latest = exclusions;
      }
      setData(latest);
      setBulkText('');
      flash(`Added ${lines.length} exclusion(s)`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type, value) => {
    setSaving(true);
    setError('');
    try {
      const params = new URLSearchParams({ type, value });
      const { exclusions } = await apiFetch(`/api/recon/exclusions?${params}`, {
        method: 'DELETE',
      });
      setData(exclusions);
      flash('Removed from exclusions');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const typeMeta = TYPES.find((t) => t.id === addType) || TYPES[0];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Ban className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Recon exclusions</h3>
          <p className="mt-1 text-sm text-slate-400">
            Oracle elements and ADP wage types listed here are <strong>skipped entirely</strong> during
            element ↔ wage type recon — no exceptions, no matched rows. Use this for items you do not want
            compared (e.g. Commission Target, memo codes).
          </p>
          <p className="mt-2 text-xs text-slate-500">
            You can also set <strong>Include in recon = No</strong> on individual mapping rows in Payroll
            mappings (excludes that Oracle ↔ ADP pair only).
          </p>
        </div>
      </div>

      {msg && <p className="text-sm text-emerald-400">{msg}</p>}
      {error && <p className="alert-error text-sm">{error}</p>}

      <form onSubmit={addOne} className="card-panel flex flex-wrap items-end gap-3 p-4">
        <label className="text-sm">
          <span className="font-medium text-slate-300">Type</span>
          <select
            value={addType}
            onChange={(e) => setAddType(e.target.value)}
            className="select-field-sm mt-1 block text-sm"
          >
            {TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="min-w-[200px] flex-1 text-sm">
          <span className="font-medium text-slate-300">Value</span>
          <input
            value={addValue}
            onChange={(e) => setAddValue(e.target.value)}
            placeholder={typeMeta.placeholder}
            className="input-field mt-1"
          />
        </label>
        <button type="submit" disabled={saving || !addValue.trim()} className="btn-primary">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add exclusion
        </button>
      </form>

      <form onSubmit={addBulk} className="card-panel space-y-2 p-4">
        <h4 className="text-sm font-semibold text-slate-100">Add many (one per line)</h4>
        <div className="flex flex-wrap gap-2">
          <select
            value={bulkType}
            onChange={(e) => setBulkType(e.target.value)}
            className="select-field-sm text-sm"
          >
            {TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <button type="submit" disabled={saving || !bulkText.trim()} className="btn-secondary">
            Add all lines
          </button>
        </div>
        <textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          rows={4}
          placeholder={'Commission Target\nMobility Allow'}
          className="input-field mt-1 font-mono text-xs"
        />
      </form>

      {loading ? (
        <div className="flex gap-2 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading exclusions…
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <ExclusionList
            title="Excluded Oracle elements"
            emptyHint="No elements excluded. Add Commission Target, etc."
            items={data.oracleElements || []}
            onRemove={(v) => remove('oracleElement', v)}
            disabled={saving}
          />
          <ExclusionList
            title="Excluded ADP wage type codes"
            emptyHint="No wage types excluded."
            items={data.adpWageTypeCodes || []}
            onRemove={(v) => remove('adpWageTypeCode', v)}
            disabled={saving}
            mono
          />
        </div>
      )}

      <p className="text-xs text-slate-500">
        Changes are saved automatically. Re-run recon after updates.
      </p>
    </div>
  );
}

function ExclusionList({ title, emptyHint, items, onRemove, disabled, mono }) {
  return (
    <div className="card-panel p-4">
      <h4 className="text-sm font-semibold text-slate-100">
        {title} ({items.length})
      </h4>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{emptyHint}</p>
      ) : (
        <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <li
              key={item}
              className="flex items-center justify-between gap-2 rounded border border-slate-700 bg-slate-800/60 px-2 py-1.5 text-sm"
            >
              <span className={mono ? 'font-mono text-slate-200' : 'text-slate-200'}>{item}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onRemove(item)}
                className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-red-400 disabled:opacity-40"
                title="Remove exclusion"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
